import type { TravelerProfile } from '@elara/domain/models/schemas/TravelerProfileSchema';

import { useEffect, useState } from 'react';

import { ROUTES } from '@elara/client-core/constants';
import { getCountriesList } from '@elara/client-core/lib/country-utils';
import { SUPPORT_EMAIL } from '@elara/client-core/lib/support-chat';

import {
  useCurrentUserProfile,
  useNavigate,
  useRouterState,
  useToastState,
  useUserProfileState,
} from '#states/hooks.js';
import { Breadcrumbs } from '#ui/Breadcrumbs.js';
import { DateSelectField } from '#ui/DateSelectField.js';
import {
  AddressFields,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FloatingInput,
  FloatingSelect,
  FloatingTextarea,
  Loader,
  SelectItem,
} from '#ui/index.js';
import { AppLayout, AppLayoutTitle } from '#views/layout/AppLayout.jsx';

import { PhoneVerificationField } from './PhoneVerificationField.js';

const TITLE_CLASSES = 'text-xl font-regular';
const COUNTRIES = getCountriesList();

/** Builds a complete TravelerProfile from partial data with defaults */
function buildTravelerProfile(
  existing: Partial<TravelerProfile> | undefined,
  updates: Partial<TravelerProfile>,
): TravelerProfile {
  return {
    firstName: existing?.firstName || '',
    lastName: existing?.lastName || '',
    dateOfBirth: existing?.dateOfBirth || '',
    ...existing,
    ...updates,
  };
}

export function ProfilePage() {
  const profile = useCurrentUserProfile();
  const profileState = useUserProfileState();
  const routerState = useRouterState();
  const navigate = useNavigate();
  const toastState = useToastState();

  useEffect(() => {
    if (profile) profileState.initializeEditing();
  }, [profile, profileState]);

  if (!profile) {
    return (
      <AppLayout
        backButtonRoute={ROUTES.ACCOUNT}
        title={<AppLayoutTitle title="Profile" />}
        transitionKey={routerState.currentRoute?.path}
      >
        <div className="flex items-center justify-center h-full">
          <Loader loading={true} />
        </div>
      </AppLayout>
    );
  }

  const isSaving = profileState.isSaving;
  const error = profileState.error;
  const editingProfile = profileState.editingProfile;

  const handleSave = async () => {
    await profileState.save();
    if (!profileState.error) {
      toastState.addToast({ title: 'Profile saved', variant: 'success' });
      navigate.navigate(ROUTES.ACCOUNT);
    }
  };

  return (
    <AppLayout
      backButtonRoute={ROUTES.ACCOUNT}
      title={<AppLayoutTitle title="Profile" />}
      transitionKey={routerState.currentRoute?.path}
    >
      <div className="h-full flex flex-col overflow-hidden">
        <div className="flex-1 overflow-auto">
          <div className="max-w-2xl mx-auto w-full px-4 pt-12 pb-16 flex flex-col gap-6">
            <div className="flex flex-col gap-3 mb-2">
              <Breadcrumbs items={[{ label: 'Account', to: ROUTES.ACCOUNT }, { label: 'Profile' }]} className="mb-3" />
              <h1 className="text-2xl xl:text-3xl font-display font-light">Profile</h1>
              <p className="text-muted-foreground">Manage your personal information.</p>
            </div>

            {error && (
              <div className="bg-destructive/10 text-destructive px-4 py-2 rounded-md text-sm mb-6">
                {error.message}
              </div>
            )}

            <Card className="shadow-sm p-6 dark:bg-background-secondary">
              <CardHeader className="sr-only">
                <CardTitle className={TITLE_CLASSES}>Personal details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <FloatingInput
                        id="firstName"
                        label="First name"
                        value={editingProfile.travelerProfile?.firstName || ''}
                        onChange={(e) => {
                          const updated = buildTravelerProfile(editingProfile.travelerProfile, {
                            firstName: e.target.value,
                          });
                          profileState.updateField('travelerProfile', updated);
                          profileState.updateField(
                            'displayName',
                            [e.target.value, updated.lastName].filter(Boolean).join(' '),
                          );
                        }}
                      />
                      <FloatingInput
                        id="lastName"
                        label="Last name"
                        value={editingProfile.travelerProfile?.lastName || ''}
                        onChange={(e) => {
                          const updated = buildTravelerProfile(editingProfile.travelerProfile, {
                            lastName: e.target.value,
                          });
                          profileState.updateField('travelerProfile', updated);
                          profileState.updateField(
                            'displayName',
                            [updated.firstName, e.target.value].filter(Boolean).join(' '),
                          );
                        }}
                      />
                    </div>

                    <div className="space-y-2">
                      <FloatingInput id="email" label="Email" value={editingProfile.email} readOnly disabled />
                      <p className="text-sm text-muted-foreground">
                        Please{' '}
                        <a href={`mailto:${SUPPORT_EMAIL}`} className="text-accent hover:underline">
                          contact support
                        </a>{' '}
                        if you need to change your email address.
                      </p>
                    </div>

                    <DateSelectField
                      id="dateOfBirth"
                      label="Date of birth"
                      value={editingProfile.travelerProfile?.dateOfBirth || ''}
                      onChange={(value) =>
                        profileState.updateField(
                          'travelerProfile',
                          buildTravelerProfile(editingProfile.travelerProfile, { dateOfBirth: value }),
                        )
                      }
                    />
                  </div>

                  <PhoneVerificationField />

                  <AddressFields
                    value={editingProfile.address ?? null}
                    onChange={(address) => profileState.updateAddress(address)}
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-sm p-6 dark:bg-background-secondary">
              <CardHeader>
                <CardTitle className={TITLE_CLASSES}>Passport details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <FloatingSelect
                    label="Nationality"
                    value={editingProfile.travelerProfile?.passportInfo?.nationality || ''}
                    onValueChange={(value) =>
                      profileState.updateField(
                        'travelerProfile',
                        buildTravelerProfile(editingProfile.travelerProfile, {
                          passportInfo: { ...editingProfile.travelerProfile?.passportInfo, nationality: value },
                        }),
                      )
                    }
                    id="nationality"
                  >
                    {COUNTRIES.map((country) => (
                      <SelectItem key={country.code} value={country.name}>
                        {country.name}
                      </SelectItem>
                    ))}
                  </FloatingSelect>
                  <div className="grid grid-cols-2 gap-3">
                    <FloatingInput
                      id="passportNumber"
                      label="Passport number"
                      value={editingProfile.travelerProfile?.passportInfo?.number || ''}
                      onChange={(e) =>
                        profileState.updateField(
                          'travelerProfile',
                          buildTravelerProfile(editingProfile.travelerProfile, {
                            passportInfo: { ...editingProfile.travelerProfile?.passportInfo, number: e.target.value },
                          }),
                        )
                      }
                    />
                    <DateSelectField
                      id="passportExpiry"
                      label="Passport expiry"
                      mode="future"
                      value={editingProfile.travelerProfile?.passportInfo?.expiryDate || ''}
                      onChange={(value) =>
                        profileState.updateField(
                          'travelerProfile',
                          buildTravelerProfile(editingProfile.travelerProfile, {
                            passportInfo: { ...editingProfile.travelerProfile?.passportInfo, expiryDate: value },
                          }),
                        )
                      }
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <FormButtonsBar>
              <div className="self-start flex-1">
                <DeleteAccountSection />
              </div>
              <Button variant="outline" onClick={() => navigate.navigate(ROUTES.ACCOUNT)} disabled={isSaving}>
                Cancel
              </Button>
              <Button onClick={handleSave} isBusy={isSaving}>
                Save
              </Button>
            </FormButtonsBar>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

function FormButtonsBar({ children }: { children: React.ReactNode }) {
  return (
    <div className="sticky bottom-0 left-0 right-0 py-4 bg-background-secondary/60 backdrop-blur-md -mx-4 z-20">
      <div className="flex justify-end gap-3 max-w-2xl mx-auto w-full px-4">{children}</div>
    </div>
  );
}

/**
 * Delete-account section pinned to the bottom of the account screen (ELARA-732). Elara is a
 * subscription service, so this doesn't hard-delete — it submits a request that support reviews before
 * an admin removes the account. Delegates to the shared `UserProfileState.requestAccountDeletion()`
 * (the same call the mobile sheet uses). Once requested, `hasRequestedDeletion` keeps the pending notice.
 */
export function DeleteAccountSection() {
  const userProfileState = useUserProfileState();
  const toastState = useToastState();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState('');

  const isPending = userProfileState.hasRequestedDeletion;
  const isSubmitting = userProfileState.deletionRequestState === 'submitting';

  async function handleConfirm() {
    await userProfileState.requestAccountDeletion(reason);
    setDialogOpen(false);
    setReason('');
    toastState.addToast({
      title: 'Request received',
      description: 'We’ll email you to confirm your account deletion soon.',
      variant: 'success',
    });
    navigate.navigate(ROUTES.HOME);
  }

  return (
    <div className="mt-2 flex flex-col gap-3">
      {isPending ? (
        <div className="text-muted-foreground text-sm">Account deletion pending</div>
      ) : (
        <Button variant="link" className="text-destructive self-start" onClick={() => setDialogOpen(true)}>
          Delete your account
        </Button>
      )}

      <Dialog open={dialogOpen} onOpenChange={(open) => !open && !isSubmitting && setDialogOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete your account</DialogTitle>
            <DialogDescription>
              This permanently deletes your Elara account and all your data — trips, chats, and saved details. Our team
              will email you to confirm before anything is removed. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <FloatingTextarea
            id="delete-account-reason"
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={2000}
            rows={3}
            disabled={isSubmitting}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirm} isBusy={isSubmitting} disabled={isSubmitting}>
              Request account deletion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
